import React from 'react';
import { useContent } from '../content/ContentContext';
import { Performance } from '../content/types';
import { extractYouTubeId } from './youtube';

// Edit-mode view of the performance videos: per-video Title / Performer /
// YouTube fields with a live preview, reorder + remove, and add. Writes the
// whole `performances` array as one override on each change.
export function PerformanceEditor() {
  const { content, setField } = useContent();
  const items = content.performances;

  const commit = (next: Performance[]) => { setField('performances', next); };

  const update = (i: number, patch: Partial<Performance>) =>
    commit(items.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const remove = (i: number) => commit(items.filter((_, idx) => idx !== i));

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    commit(next);
  };

  const add = () => {
    const id = 'video-' + (globalThis.crypto?.randomUUID?.() ?? String(Date.now()));
    commit([...items, { id, title: '', meta: '', videoId: '' }]);
  };

  return (
    <div className="perf-editor">
      {items.map((p, i) => (
        <div key={p.id} className="perf-editor__row">
          <div className="perf-editor__preview">
            {p.videoId ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${p.videoId}?rel=0`}
                title={p.title || 'preview'}
                loading="lazy"
                allow="encrypted-media; picture-in-picture"
              />
            ) : (
              <div className="perf-editor__empty">paste a YouTube link below</div>
            )}
          </div>

          <div className="perf-editor__fields">
            <label>
              <span>Title</span>
              <input value={p.title} onChange={e => update(i, { title: e.target.value })} placeholder="Dvořák · Cello Concerto" />
            </label>
            <label>
              <span>Performer / note</span>
              <input value={p.meta} onChange={e => update(i, { meta: e.target.value })} placeholder="Yo-Yo Ma · Berliner Philharmoniker" />
            </label>
            <label>
              <span>YouTube link or ID</span>
              <input
                value={p.videoId}
                onChange={e => update(i, { videoId: extractYouTubeId(e.target.value) })}
                placeholder="https://youtu.be/…"
              />
            </label>
            <div className="perf-editor__actions">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down">↓</button>
              <button type="button" className="perf-editor__remove" onClick={() => remove(i)}>Remove</button>
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="btn" onClick={add}>+ Add video</button>
    </div>
  );
}
